import React, {useContext, useEffect, useState} from "react";
import {Select} from "antd";
import {AppContextContext, OrganizationServiceContext} from "../../Contexts";
import Organization from "../../domain/Organization";
import {ResourceOwnerType} from "../../domain/ResourceOwnerType";
import QueryOptions from "../../sal-ui/QueryOptions";
import {FormPersister} from "../../sal-ui/FormPersister";
import {FilterItem, FilterItemProps, SelectEditor} from "../tablefilter/TableFilter";
import {SearchFilter} from "../tablefilter/SearchFilter";
import * as styles from "./OwnerFilter.module.css";

const SYSTEM_OWNER_FILTER_VALUE = "__SYSTEM__";

interface OwnerOption extends Organization {
    system?: boolean;
}

interface OwnerItemProps extends FilterItemProps {
    owners?: OwnerOption[];
    loading: boolean;
}

export interface OwnerFilterAccess {
    canReadSystem: boolean;
    canReadOrganization: boolean;
}

interface OwnerFilterProps extends OwnerFilterAccess {
    immediateMode?: boolean;
    className?: string;
    values?: any;
    defaultValues: any;
    persistentStateIdent?: string;
    onChange: (values: any) => void;
}

interface OwnerFilterLocation {
    pathname: string;
    search: string;
}

function OwnerItem(props: OwnerItemProps) {
    if (props.loading && props.value?.length > 0) {
        return <LoadingFilterValue/>;
    }

    const systemOwners = props.owners?.filter(owner => owner.system) ?? [];
    const organizationOwners = props.owners?.filter(owner => !owner.system) ?? [];

    return (
        <SelectEditor
            width={180}
            popupMatchSelectWidth={220}
            multiple={true}
            showSearch={true}
            optionLabelProp="label"
            optionFilterProp="label"
            value={props.value}
            onChange={value => props.onChange(value)}
        >
            {systemOwners.length > 0 &&
                <Select.OptGroup label="System">
                    {systemOwners.map(owner => (
                        <Select.Option key={owner.id} value={owner.id} label={owner.name}>
                            {owner.name}
                        </Select.Option>
                    ))}
                </Select.OptGroup>
            }

            {organizationOwners.length > 0 &&
                <Select.OptGroup label="Organizations">
                    {organizationOwners.map(owner => (
                        <Select.Option key={owner.id} value={owner.id} label={owner.name}>
                            {owner.name}
                        </Select.Option>
                    ))}
                </Select.OptGroup>
            }
        </SelectEditor>
    );
}

function LoadingFilterValue() {
    return <span className={styles["loading-filter-value"]}>Loading...</span>;
}

function OwnerFilter(props: OwnerFilterProps) {
    const appContext = useContext(AppContextContext);
    const organizationService = useContext(OrganizationServiceContext);
    const [owners, setOwners] = useState<OwnerOption[]>();
    const [ownersLoading, setOwnersLoading] = useState(true);

    useEffect(() => {
        if (!appContext.user.isSystemAdmin()) {
            setOwners([
                ...systemOwnerOptions(),
                ...ownOrganizationOptions()
            ]);
            setOwnersLoading(false);

            return;
        }

        setOwnersLoading(true);

        organizationService.getList(QueryOptions.newUnlimitedOrderedInstance("name"))
            .then(result => setOwners([
                systemOwner(),
                ...result.data
            ]))
            .finally(() => setOwnersLoading(false));
    }, [appContext.user, organizationService, props.canReadSystem, props.canReadOrganization]);

    const items: FilterItem[] = [
        {
            name: "ownerIds",
            key: "ownerIds",
            label: "Owner",
            render: itemProps => <OwnerItem {...itemProps} owners={owners} loading={ownersLoading}/>
        }
    ];

    return (
        <SearchFilter
            items={items}
            className={props.className}
            values={props.values}
            defaultValues={props.defaultValues}
            persistentStateIdent={props.persistentStateIdent}
            immediateMode={props.immediateMode}
            defaultActiveFilterNames={["ownerIds"]}
            onChange={props.onChange}
        />
    );

    function systemOwnerOptions() {
        return props.canReadSystem
            ? [systemOwner()]
            : [];
    }

    function ownOrganizationOptions() {
        return props.canReadOrganization && appContext.user.organization
            ? [appContext.user.organization]
            : [];
    }
}

export function createDefaultOwnerFilterValues(appContext: any, access: OwnerFilterAccess) {
    if (appContext.user.isSystemAdmin()) {
        return {ownerIds: [SYSTEM_OWNER_FILTER_VALUE]};
    }

    if (access.canReadOrganization && appContext.user.organization?.id) {
        return {ownerIds: [
            ...(access.canReadSystem ? [SYSTEM_OWNER_FILTER_VALUE] : []),
            appContext.user.organization.id
        ]};
    }

    if (access.canReadSystem) {
        return {ownerIds: [SYSTEM_OWNER_FILTER_VALUE]};
    }

    return {ownerIds: []};
}

function normalizeOwnerFilterValues(values: any) {
    if (!values || values.ownerIds || !values.organizationIds) {
        return values;
    }

    const normalizedValues = {...values, ownerIds: values.organizationIds};

    delete normalizedValues.organizationIds;

    return normalizedValues;
}

interface LoadPersistentOwnerFilterValuesOptions {
    appContext: any;
    access: OwnerFilterAccess;
    filterFormPersister: FormPersister;
    location: OwnerFilterLocation;
    navigate: (to: string, options: {replace: boolean}) => void;
}

export function loadPersistentOwnerFilterValues(options: LoadPersistentOwnerFilterValuesOptions) {
    const {appContext, access, filterFormPersister, location, navigate} = options;
    const searchParams = new URLSearchParams(location.search);
    const loadedValues = normalizeOwnerFilterValues(searchParams.has("filter")
        ? filterFormPersister.loadValues(FormPersister.decodeUrlState(searchParams.get("filter")!))
        : filterFormPersister.loadValues());

    const defaultValues = createDefaultOwnerFilterValues(appContext, access);
    const values = isEmptyValues(loadedValues)
        ? defaultValues
        : {...defaultValues, ...loadedValues};

    if (searchParams.has("filter")) {
        navigate(location.pathname, {replace: true});
    }

    return values;
}

export function createOwnerRqbQuery(values: any) {
    const rqbQuery = {
        combinator: "and",
        rules: [] as any[]
    };

    const ownerIds: string[] = values?.ownerIds || [];
    const includeSystem = ownerIds.includes(SYSTEM_OWNER_FILTER_VALUE);
    const selectedOrganizationIds = ownerIds.filter(ownerId => ownerId !== SYSTEM_OWNER_FILTER_VALUE);
    const ownerRules: any[] = [];

    if (includeSystem) {
        ownerRules.push({
            field: "owner",
            operator: "eq",
            value: ResourceOwnerType.SYSTEM
        });
    }

    if (selectedOrganizationIds.length > 0) {
        ownerRules.push({
            field: "organization.id",
            operator: "in",
            value: selectedOrganizationIds
        });
    }

    if (ownerRules.length === 1) {
        rqbQuery.rules.push(ownerRules[0]);
    } else if (ownerRules.length > 1) {
        rqbQuery.rules.push({
            combinator: "or",
            rules: ownerRules
        });
    }

    return rqbQuery;
}

function systemOwner(): OwnerOption {
    const owner = new Organization(SYSTEM_OWNER_FILTER_VALUE, "System") as OwnerOption;

    owner.system = true;

    return owner;
}

function isEmptyValues(values: any) {
    return !values || Object.keys(values).length === 0;
}

export default OwnerFilter;
